import React from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, User as UserIcon, BadgeCheck, Calendar, Shield, CrownIcon } from 'lucide-react';
import { motion } from 'framer-motion';

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1
    }
  }
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 10
    }
  }
};

export function UserDetails({ userDetails, emailVerified, onVerifyEmail }) {
  const { t } = useTranslation();

  const details = [
    {
      icon: UserIcon,
      label: t('userDetails.name.label'),
      value: userDetails?.name || t('userDetails.name.notSpecified'),
      color: 'text-blue-600 dark:text-blue-400',
      bgColor: 'bg-blue-50 dark:bg-blue-900/30'
    },
    {
      icon: Mail,
      label: t('userDetails.email.label'),
      value: userDetails?.email || t('userDetails.email.notSpecified'),
      color: 'text-purple-600 dark:text-purple-400',
      bgColor: 'bg-purple-50 dark:bg-purple-900/30'
    },
    {
      icon: Calendar,
      label: t('userDetails.joined.label'),
      value: userDetails?.createdAt ? new Date(userDetails.createdAt).toLocaleDateString() : t('userDetails.joined.notSpecified'),
      color: 'text-emerald-600 dark:text-emerald-400',
      bgColor: 'bg-emerald-50 dark:bg-emerald-900/30'
    },
    {
      icon: CrownIcon,
      label: t('userDetails.premium.label'),
      value: userDetails?.isPremium || t('userDetails.premium.notSpecified'),
      color: 'text-yellow-600 dark:text-yellow-400',
      bgColor: 'bg-yellow-50 dark:bg-yellow-900/30'
    }
  ];

  return (
    <motion.section
      variants={container}
      initial="hidden"
      animate="show"
      className="w-full max-w-3xl mx-auto p-4 sm:p-6"
    >
      <motion.div
        variants={item}
        className="flex items-center justify-between mb-6"
      >
        <h2 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white flex items-center">
          {t('userDetails.title')}
          {emailVerified && (
            <BadgeCheck className="w-5 h-5 ml-2 text-green-500 dark:text-green-400" />
          )}
        </h2>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {details.map((detail, index) => {
          const Icon = detail.icon;
          return (
            <motion.div
              key={index}
              variants={item}
              className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-md transition-all duration-300 hover:translate-y-[-2px]"
            >
              <div className="flex items-center space-x-4">
                <div className={`p-3 rounded-full ${detail.bgColor}`}>
                  <Icon className={`w-5 h-5 ${detail.color}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    {detail.label}
                  </p>
                  <p className="mt-1 text-base font-medium text-gray-900 dark:text-white truncate">
                    {detail.value}
                  </p>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* User Status Card */}
      <motion.div
        variants={item}
        className="mt-6 p-5 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-800/50 
          rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm"
      >
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center space-x-3">
            <div className={`h-3 w-3 rounded-full ${emailVerified
              ? 'bg-green-500 dark:bg-green-400'
              : 'bg-yellow-500 dark:bg-yellow-400'
              }`} />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {emailVerified
                ? t('userDetails.status.verified')
                : t('userDetails.status.pending')
              }
            </span>
          </div>

          <div className="flex items-center space-x-2">
            <Shield className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {emailVerified
                ? t('userDetails.security.verified')
                : t('userDetails.security.verifyEmail')}
            </span>
          </div>
        </div>

        {!emailVerified && (
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            onClick={onVerifyEmail}
            className="mt-4 w-full sm:w-auto px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg text-sm transition-colors duration-200"
          >
            {t('userDetails.actions.verifyEmail')}
          </motion.button>
        )}
      </motion.div>
    </motion.section>
  );
}

export default UserDetails;